import React, { useCallback, useEffect, useState } from 'react';
import { listStoreActiveEmployees } from '../../../services/storeEmployeeApi';

const emptyPagination = { page: 1, limit: 20, total: 0, totalPages: 0, hasNext: false, hasPrev: false };
const display = (value) => (value === null || value === undefined || value === '' ? '-' : value);
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

const StoreActiveEmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEmployees = useCallback((page = 1, activeSearch = search) => {
    setLoading(true);
    setError('');
    listStoreActiveEmployees({ page, limit: pagination.limit || 20, search: activeSearch })
      .then((response) => {
        setEmployees(response.data?.items || []);
        setPagination(response.data?.pagination || emptyPagination);
      })
      .catch((err) => {
        setEmployees([]);
        setPagination((current) => ({ ...emptyPagination, limit: current.limit || 20 }));
        setError(err.message || 'Unable to fetch active employees.');
      })
      .finally(() => setLoading(false));
  }, [pagination.limit, search]);

  useEffect(() => {
    loadEmployees(1, search);
  }, [loadEmployees, search]);

  const submitSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  return (
    <div className="card p-3 p-md-4">
      <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
        <div>
          <h3 className="mb-1">Active Employees</h3>
          <p className="text-muted mb-0">Employees currently active in your outlet.</p>
        </div>
      </div>

      <form className="row g-2 mb-3" onSubmit={submitSearch}>
        <div className="col-12 col-md">
          <input
            className="form-control"
            placeholder="Search by employee ID, name, designation or mobile"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            disabled={loading}
          />
        </div>
        <div className="col-6 col-md-auto">
          <button className="btn btn-primary w-100" type="submit" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
        <div className="col-6 col-md-auto">
          <button className="btn btn-outline-secondary w-100" type="button" onClick={clearSearch} disabled={loading || (!search && !searchInput)}>
            Clear
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>S.No</th><th>Temporary Employee ID</th><th>Permanent Employee ID</th><th>Employee Name</th><th>Designation</th><th>Mobile</th><th>Gender</th><th>Date of Joining</th><th>Employment Type</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" />Loading employees...</td></tr>
            ) : employees.length ? employees.map((employee, rowIndex) => (
              <tr key={employee.id}>
                <td>{(pagination.page - 1) * pagination.limit + rowIndex + 1}</td>
                <td>{display(employee.tempEmployeeId)}</td>
                <td>{display(employee.permanentEmployeeId)}</td>
                <td>{display(employee.employeeName)}</td>
                <td>{display(employee.designationName)}</td>
                <td>{display(employee.mobile)}</td>
                <td>{display(employee.gender)}</td>
                <td>{formatDate(employee.dateOfJoin)}</td>
                <td>{display(employee.employmentType)}</td>
              </tr>
            )) : (
              <tr><td colSpan="9" className="text-center py-4">No active employees found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mt-3">
        <small className="text-muted">Page {pagination.page} of {pagination.totalPages || 1} • Total {pagination.total}</small>
        <div className="btn-group">
          <button className="btn btn-outline-primary" disabled={loading || !pagination.hasPrev} onClick={() => loadEmployees(pagination.page - 1)}>Previous</button>
          <button className="btn btn-outline-primary" disabled={loading || !pagination.hasNext} onClick={() => loadEmployees(pagination.page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default StoreActiveEmployeeList;
